import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Checkbox, Col, Descriptions, Input, InputNumber, Row, Space, Spin, Tag, Typography } from 'antd';
import engineeringMaterialService, {
  formatKg,
  type ApprovedEngineeringDefinition,
  type EngineeringConfig,
  type EngineeringLineInput,
  type EngineeringLineSnapshot,
  type EngineeringPreview,
} from '@/services/engineeringMaterial.service';

const { Text } = Typography;

export interface PrEngineeringLine {
  id?: string;
  item_code?: string;
  quantity: number;
  uom?: string | null;
  piece_quantity?: number | null;
  measurement_type?: string | null;
  length_mm?: number | null;
  width_mm?: number | null;
}

interface Props {
  config: EngineeringConfig;
  line: PrEngineeringLine;
  latest?: ApprovedEngineeringDefinition;
  saved: EngineeringLineSnapshot | null;
  input?: EngineeringLineInput;
  canOverride: boolean;
  onInputChange: (input: EngineeringLineInput) => void;
  onPreview: (weight: number | null) => void;
}

const REASON_TEXT: Record<string, string> = {
  unsupported_uom: 'The line UOM cannot be converted to a weight (use pieces, a length, an area or a mass unit).',
  area_basis_requires_plate: 'Area quantities can only be weighed for plate.',
};

function reasonText(reason?: string): string {
  if (!reason) return '';
  if (reason.startsWith('missing_dimension:')) return `Enter: ${reason.split(':')[1].split(',').join(', ')}`;
  return REASON_TEXT[reason] ?? reason;
}

/** Material weight calculator for one PR line. The backend computes; this only previews and collects inputs. */
export const PrLineEngineeringPanel: React.FC<Props> = ({
  config,
  line,
  latest,
  saved,
  input,
  canOverride,
  onInputChange,
  onPreview,
}) => {
  const sameItem = Boolean(saved && saved.definition.item_code === line.item_code);
  const frozen = sameItem && !input?.refresh ? saved!.definition : latest;
  const profile = config.profiles.find((p) => p.code === frozen?.profile_code);
  const measured = Boolean(line.measurement_type) && line.piece_quantity != null;
  const editableParams = useMemo(() => {
    if (!profile || frozen?.calc_method === 'MANUAL') return [];
    const section = frozen?.dimensions_variable ? [...profile.params, ...profile.optional_params] : [];
    return measured ? section : [...section, 'length'];
  }, [profile, frozen, measured]);

  const dims: Record<string, number | null> = {
    ...(frozen?.standard_dimensions ?? {}),
    ...(sameItem ? saved!.inputs.dimensions : {}),
    ...(input?.dimensions ?? {}),
  };
  const density = input && 'density_override' in input ? input.density_override : sameItem ? saved!.inputs.density_override : undefined;
  const manual =
    input && 'manual_unit_weight' in input ? input.manual_unit_weight : sameItem ? saved!.inputs.manual_unit_weight : undefined;

  const [preview, setPreview] = useState<EngineeringPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  const requestKey = JSON.stringify([line, input ?? null]);
  useEffect(() => {
    if (!line.item_code || (!latest && !sameItem)) return;
    const n = ++seq.current;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await engineeringMaterialService.calculate({
          item_code: line.item_code!,
          pr_item_id: line.id ?? null,
          engineering_input: input ?? null,
          quantity: line.quantity,
          uom: line.uom,
          piece_quantity: line.piece_quantity ?? null,
          measurement_type: line.measurement_type ?? null,
          length_mm: line.length_mm ?? null,
          width_mm: line.width_mm ?? null,
        });
        if (n !== seq.current) return;
        setPreview(res);
        setError(null);
        onPreview(res.theoretical_weight);
      } catch (e: any) {
        if (n !== seq.current) return;
        setPreview(null);
        setError(e?.response?.data?.message || e?.message || 'Calculation failed');
        onPreview(null);
      } finally {
        if (n === seq.current) setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [requestKey]);

  if (!frozen) return null;

  const setDim = (p: string, v: number | null) =>
    onInputChange({ ...(input ?? {}), dimensions: { ...(input?.dimensions ?? (sameItem ? saved!.inputs.dimensions : {})), [p]: v } });
  const setOverride = (key: 'density_override' | 'manual_unit_weight', value: number | null, reason?: string) =>
    onInputChange({ ...(input ?? {}), [key]: value == null ? null : { value, reason: reason ?? '' } });

  const result = preview?.snapshot.result;
  const newerRevision = sameItem && latest && latest.revision > saved!.definition.revision;

  return (
    <div style={{ padding: '8px 0' }}>
      <Space align="center" style={{ marginBottom: 8 }} wrap>
        <Text strong>Material weight calculator</Text>
        <Tag>
          {profile?.label ?? frozen.profile_code} · rev {frozen.revision}
        </Tag>
        {frozen.grade ? <Tag color="blue">{`${frozen.grade.grade_code} · ${frozen.density_g_cm3} g/cm³`}</Tag> : null}
        {frozen.section ? <Tag color="blue">{`${frozen.section.designation} · ${frozen.kg_per_m} kg/m`}</Tag> : null}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Information only — quantities, stock and valuation are not changed.
        </Text>
      </Space>

      {newerRevision ? (
        <Checkbox
          checked={Boolean(input?.refresh)}
          onChange={(e) => onInputChange({ ...(input ?? {}), refresh: e.target.checked })}
          style={{ display: 'block', marginBottom: 8 }}
        >
          Use the latest approved definition (rev {latest!.revision}) instead of rev {saved!.definition.revision}
        </Checkbox>
      ) : null}

      <Row gutter={12}>
        {editableParams.map((p) => (
          <Col key={p} xs={12} md={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {config.param_labels[p] ?? p} (mm)
            </Text>
            <InputNumber
              size="small"
              min={0}
              style={{ width: '100%' }}
              value={dims[p] ?? undefined}
              onChange={(v) => setDim(p, v == null ? null : Number(v))}
            />
          </Col>
        ))}
        {measured ? (
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Piece length / width come from the line dimensions.
            </Text>
          </Col>
        ) : null}
      </Row>

      {canOverride || density || manual ? (
        <Row gutter={12} style={{ marginTop: 8 }}>
          {frozen.calc_method === 'GEOMETRY' ? (
            <Col xs={24} md={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Density override (g/cm³) — approved {frozen.density_g_cm3}
              </Text>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  size="small"
                  min={0.0001}
                  max={25}
                  disabled={!canOverride}
                  style={{ width: 120 }}
                  value={density?.value ?? undefined}
                  onChange={(v) => setOverride('density_override', v == null ? null : Number(v), density?.reason ?? '')}
                />
                <Input
                  size="small"
                  disabled={!canOverride || density?.value == null}
                  placeholder="Reason (required)"
                  value={density?.reason ?? ''}
                  onChange={(e) => setOverride('density_override', density?.value ?? null, e.target.value)}
                />
              </Space.Compact>
            </Col>
          ) : null}
          <Col xs={24} md={12}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Manual unit weight (kg) — replaces the calculation
            </Text>
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber
                size="small"
                min={0}
                disabled={!canOverride}
                style={{ width: 120 }}
                value={manual?.value ?? undefined}
                onChange={(v) => setOverride('manual_unit_weight', v == null ? null : Number(v), manual?.reason ?? '')}
              />
              <Input
                size="small"
                disabled={!canOverride || manual?.value == null}
                placeholder="Reason (required)"
                value={manual?.reason ?? ''}
                onChange={(e) => setOverride('manual_unit_weight', manual?.value ?? null, e.target.value)}
              />
            </Space.Compact>
          </Col>
        </Row>
      ) : null}

      <div style={{ marginTop: 8 }}>
        <Spin spinning={loading} size="small">
          {error ? (
            <Alert type="error" showIcon message={error} />
          ) : result ? (
            result.status === 'CALCULATED' ? (
              <Descriptions size="small" column={{ xs: 1, md: 4 }}>
                {result.kg_per_m != null ? (
                  <Descriptions.Item label="kg/m">{result.kg_per_m.toFixed(4)}</Descriptions.Item>
                ) : null}
                <Descriptions.Item label="Unit weight">{formatKg(result.unit_weight_kg, 4)}</Descriptions.Item>
                <Descriptions.Item label="Basis">
                  {result.basis} · {result.physical_quantity} {result.physical_uom ?? ''}
                </Descriptions.Item>
                <Descriptions.Item label="Theoretical weight">
                  <Text strong>{formatKg(result.total_weight_kg)}</Text>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert type="warning" showIcon message={reasonText(result.reason)} />
            )
          ) : null}
          {result?.warnings?.length ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {result.warnings.join(' ')}
            </Text>
          ) : null}
        </Spin>
      </div>
    </div>
  );
};

export default PrLineEngineeringPanel;
